import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { Button } from "@twilio-paste/core/button";
import { Spinner } from "@twilio-paste/core/spinner";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Header } from "../Header";
import contactBackend from "../../utils/contactBackend";
import { AppState, EngagementPhase } from "../../store/definitions";
import { changeEngagementPhase } from "../../store/actions/genericActions";
import { recentChatsStorage } from "../../utils/recentChatsStorage";
import {
    formatHistoryAuthorLabel,
    HistoryParticipantInfo
} from "../../utils/formatHistoryAuthorLabel";

type HistoryMessage = {
    sid: string;
    author?: string;
    body?: string;
    dateCreated?: string;
};

type HistoryConversation = {
    conversationSid: string;
    state?: string;
    dateCreated?: string;
    dateUpdated?: string;
    friendlyName?: string;
    serviceType?: string;
    participants?: HistoryParticipantInfo[];
};

const formatDateTime = (value?: string) => {
    if (!value) {
        return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString("zh-CN");
};

/** Flex WebChat：客人 identity 多为 FX…；会话 SID 作 author 为系统欢迎语；其余多为坐席 */
const isCustomerAuthor = (author?: string, conversationSid?: string) => {
    if (!author) {
        return false;
    }
    if (conversationSid && author === conversationSid) {
        return false;
    }
    if (author === "Concierge") {
        return false;
    }
    return author.startsWith("FX");
};

export const ChatHistoryPhase = () => {
    const dispatch = useDispatch();
    const conversationSid = useSelector((state: AppState) => state.session.selectedHistoryConversationSid);
    const email = useSelector((state: AppState) => state.session.preEngagementData?.Email);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [conversation, setConversation] = useState<HistoryConversation | null>(null);
    const [messages, setMessages] = useState<HistoryMessage[]>([]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!conversationSid) {
                setError("未选择会话");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");
            try {
                const resp = await contactBackend<{
                    conversation?: HistoryConversation;
                    messages?: HistoryMessage[];
                    message?: string;
                }>("/chatMessages", { conversationSid });

                if (cancelled) {
                    return;
                }

                setConversation(resp.conversation || { conversationSid });
                setMessages(resp.messages || []);

                if (email && resp.conversation) {
                    const lastMessage = (resp.messages || [])[(resp.messages || []).length - 1];
                    recentChatsStorage.upsert(email, {
                        conversationSid,
                        state: resp.conversation.state,
                        dateCreated: resp.conversation.dateCreated,
                        dateUpdated: resp.conversation.dateUpdated,
                        friendlyName: resp.conversation.friendlyName,
                        serviceType: resp.conversation.serviceType,
                        preview: lastMessage?.body || ""
                    });
                }
            } catch (e) {
                if (!cancelled) {
                    setError("加载聊天记录失败，请稍后重试");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [conversationSid, email]);

    const handleBack = () => {
        dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
    };

    return (
        <Box display="flex" flexDirection="column" height="100vh" overflow="hidden">
            <Header />
            <Box
                padding="space50"
                borderBottomStyle="solid"
                borderBottomWidth="borderWidth10"
                borderBottomColor="colorBorderWeak"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                columnGap="space40"
            >
                <Box>
                    <Text as="h3" fontSize="fontSize40" fontWeight="fontWeightSemibold">
                        {conversation?.serviceType || "聊天记录"}
                    </Text>
                    <Text as="p" color="colorTextWeak" fontSize="fontSize20" marginTop="space20">
                        {formatDateTime(conversation?.dateUpdated || conversation?.dateCreated)}
                        {conversation?.state ? ` · ${conversation.state}` : ""}
                    </Text>
                </Box>
                <Button variant="secondary" onClick={handleBack}>
                    返回
                </Button>
            </Box>

            <Box flex="1" overflowY="auto" padding="space50" backgroundColor="colorBackgroundBody">
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="size20">
                        <Spinner decorative size="sizeIcon40" title="加载聊天记录" />
                    </Box>
                ) : null}

                {!loading && error ? (
                    <Text as="p" color="colorTextError">
                        {error}
                    </Text>
                ) : null}

                {!loading && !error && messages.length === 0 ? (
                    <Text as="p" color="colorTextWeak">
                        该会话暂无消息。
                    </Text>
                ) : null}

                {!loading &&
                    !error &&
                    messages.map((message) => {
                        const mine = isCustomerAuthor(message.author, conversationSid);
                        return (
                            <Box
                                key={message.sid}
                                display="flex"
                                justifyContent={mine ? "flex-end" : "flex-start"}
                                marginBottom="space40"
                            >
                                <Box
                                    maxWidth="80%"
                                    padding="space40"
                                    borderRadius="borderRadius30"
                                    backgroundColor={mine ? "colorBackgroundPrimaryWeakest" : "colorBackground"}
                                >
                                    <Text as="p" fontSize="fontSize20" color="colorTextWeak" marginBottom="space20">
                                        {formatHistoryAuthorLabel(message.author, conversationSid, {
                                            participants: conversation?.participants,
                                            customerEmail: email
                                        })} ·{" "}
                                        {formatDateTime(message.dateCreated)}
                                    </Text>
                                    <Text as="p" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                        {message.body || "(附件或其他内容)"}
                                    </Text>
                                </Box>
                            </Box>
                        );
                    })}
            </Box>
        </Box>
    );
};
